import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import moment from 'moment';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { DashboardOverviewDateRange } from '@reducers/v2/dashboard/types';
import { selectDashboardDateRangeSelection, setDashboardDateRangeSelection } from '@reducers/v2/dashboard/dashboard.slice';
import { Popup, localTime } from '@helpers';
import { DATE_FORMAT } from '@v2/pages/common/constants/base.constants';
import Calendar from '@components/common/Calendar';

import { computeDashboardDateRange } from '@v2/pages/dashboard/modules/overview/components/DashboardOverview/helpers/computeDashboardDateRange';

export const useManageDashboardOverviewHeaderDateRangePicker = () => {
  const dispatch = useAppDispatch();
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const { dateRange: selectedDateRange, from: selectedFrom, to: selectedTo } = useSelector(selectDashboardDateRangeSelection);

  const applyDateRange = useCallback(
    (dateRange: DashboardOverviewDateRange) => {
      const range = computeDashboardDateRange(dateRange);
      if (!range) return;
      dispatch(setDashboardDateRangeSelection({ dateRange, ...range }));
      navigation.setParams({ dateRange });
    },
    [dispatch, navigation]
  );

  const openCustomCalendar = useCallback(() => {
    Popup({
      Component: (p: any) => (
        <Calendar
          {...{
            ...p,
            mode: 'range',
            setSelectedChartDate: () => {},
            selectedChartDate: localTime().format(),
            selectedRange: selectedFrom && selectedTo ? { start: selectedFrom, end: selectedTo } : undefined,
            selectRange: ({ start, end }: { start: string; end: string }) => {
              const from = moment(start).format(DATE_FORMAT);
              const to = moment(end).format(DATE_FORMAT);
              dispatch(setDashboardDateRangeSelection({ dateRange: DashboardOverviewDateRange.CUSTOM, from, to }));
              navigation.setParams({ dateRange: DashboardOverviewDateRange.CUSTOM, from, to });
            },
          }}
        />
      ),
      avoidingViewEnable: false,
    });
  }, [dispatch, navigation, selectedFrom, selectedTo]);

  useFocusEffect(
    useCallback(() => {
      const urlDateRange = route.params?.dateRange as DashboardOverviewDateRange | undefined;
      if (urlDateRange && urlDateRange !== selectedDateRange && Object.values(DashboardOverviewDateRange).includes(urlDateRange)) {
        if (urlDateRange === DashboardOverviewDateRange.CUSTOM) {
          const urlFrom = route.params?.from as string | undefined;
          const urlTo = route.params?.to as string | undefined;
          if (urlFrom && urlTo) {
            dispatch(setDashboardDateRangeSelection({ dateRange: DashboardOverviewDateRange.CUSTOM, from: urlFrom, to: urlTo }));
          }
          return;
        }
        applyDateRange(urlDateRange);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [route.params?.dateRange, route.params?.from, route.params?.to])
  );

  const onSelectDateRange = useCallback(
    (dateRange: DashboardOverviewDateRange) => {
      if (dateRange === DashboardOverviewDateRange.CUSTOM) {
        openCustomCalendar();
        return;
      }
      applyDateRange(dateRange);
    },
    [applyDateRange, openCustomCalendar]
  );

  return { selectedDateRange, onSelectDateRange };
};
